import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldLabel } from '@/components/ui/field';

import { AnatomyFields } from './AnatomyFields';
export { anatomyRow } from './AnatomyFields';

export type Measurement = {
  value: number;
  unit: string;
  gaDays?: number;
  percentile?: number;
  source?: string;
  needsReview?: boolean;
};
export type ReportPayload = {
  templateId: string;
  status: 'DRAFT';
  studyInstanceUid: string;
  fetusId: string;
  fields: Record<string, string | number | Measurement>;
};
export type FieldDefinition = {
  label: string;
  kind: string;
  readOnly?: boolean;
  schema: { type: string; enum?: number[]; properties?: Record<string, { enum?: string[] }> };
};
export type ReportForm = {
  layout_id: string;
  title: string;
  report: ReportPayload;
  field_definitions: Record<string, FieldDefinition>;
  flags: string[];
  fetus_count: number;
  presentation?: {
    clinic_name: string;
    clinic_details: string;
    logo_url: string | null;
    signature: { name: string; credentials: string; signed_at: string; image_url: string; text?: string } | null;
  };
};
const header = new Set(
  'patientName patientId dob examDate accessionNumber clinicName clinicDetails referringPhysician sonographer readingPhysician indication method quality pregnancy fetusId reportTimestamp'.split(
    ' ',
  ),
);
const dating = new Set(
  'lmp conceptionDate datingMethod edd gaEdd eddAua gaAua datingComments efwReference comparison'.split(' '),
);
function group(key: string, def: FieldDefinition) {
  if (header.has(key)) return 'Patient and examination';
  if (dating.has(key)) return 'Pregnancy dating';
  if (def.kind === 'measurement') {
    if (key === 'FHR') return 'Fetal Heart Rate';
    if (key === 'MVP' || key.startsWith('AFI')) return 'Amniotic Fluid Index';
    if (/^(UA_|MCA_|CPR)/.test(key)) return 'Dopplers';
    if (/^(CERVIX|PLACENTA|LEFT_OVARY|RIGHT_OVARY)/.test(key)) return 'Other Measurements';
    return 'Biometry';
  }
  if (key === 'fetalAnatomy') return 'Fetal Anatomy';
  if (key.startsWith('bpp')) return 'Biophysical profile';
  if (key === 'uaInterpretation' || key === 'dopplerComments') return 'Doppler findings';
  if (/image\dCaption/.test(key)) return 'Image captions';
  if (
    /Comments$/.test(key) ||
    /^(anatomy|calvarium|lateralVentricles|choroidPlexus|cerebellum|cisternaMagna|csp|noseLip|profile|cardiacAnatomy|fourChamber|lvot|rvot|spine|stomach|diaphragm|abdominalCordInsertion|kidneys|threeVesselAtBladder|bladder|upperExtremities|lowerExtremities|externalGenitalia)/.test(
      key,
    )
  )
    return 'Fetal Anatomy';
  return 'General findings';
}
const order = [
  'Patient and examination',
  'Pregnancy dating',
  'General findings',
  'Biometry',
  'Fetal Heart Rate',
  'Amniotic Fluid Index',
  'Dopplers',
  'Doppler findings',
  'Other Measurements',
  'Biophysical profile',
  'Image captions',
  'Fetal Anatomy',
];
export function EditableUltrasoundReport({
  form,
  report,
  disabled,
  onChange,
}: {
  form: ReportForm;
  report: ReportPayload;
  disabled: boolean;
  onChange: (report: ReportPayload) => void;
}) {
  const [showUnused, setShowUnused] = useState(false);
  const definitions = Object.entries(form.field_definitions).filter(
    ([key]) => !['authentication', 'impression', 'recommendations', 'clinicName', 'clinicDetails'].includes(key),
  );
  const fieldOrder =
    'patientName patientId dob examDate accessionNumber clinicName clinicDetails pregnancy fetusId indication referringPhysician sonographer readingPhysician method quality reportTimestamp lmp conceptionDate datingMethod edd gaEdd eddAua gaAua datingComments efwReference comparison BPD HC AC FL EFW FHR'.split(
      ' ',
    );
  definitions.sort(([a], [b]) => {
    const ai = fieldOrder.indexOf(a),
      bi = fieldOrder.indexOf(b);
    return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
  });
  function update(key: string, value: string | number | Measurement | undefined) {
    const fields = { ...report.fields };
    if (value === undefined || value === '') delete fields[key];
    else fields[key] = value;
    onChange({ ...report, fields });
  }
  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-6 border-b pb-5">
          {form.presentation?.logo_url && (
            <img
              src={form.presentation.logo_url}
              alt="Organization logo"
              className="h-30 w-60 max-w-full object-contain object-left"
            />
          )}
          <div className="ml-auto text-right">
            <p className="text-lg font-semibold">
              {form.presentation?.clinic_name || String(report.fields.clinicName || '')}
            </p>
            <p className="whitespace-pre-line text-sm">
              {form.presentation?.clinic_details || String(report.fields.clinicDetails || '')}
            </p>
          </div>
        </div>
        <h3 className="text-2xl font-semibold">{form.title}</h3>
        <p className="text-sm text-muted-foreground">
          {form.presentation?.signature ? 'Signed report' : 'Preliminary report · Not signed'} · Fetus {report.fetusId}
        </p>
      </header>
      <div className="flex justify-end">
        <Button type="button" variant="outline" disabled={disabled} onClick={() => setShowUnused((v) => !v)}>
          {showUnused ? 'Hide unused fields' : 'Add missing report fields'}
        </Button>
      </div>
      {order.map((section) => {
        const rows = definitions.filter(
          ([key, def]) => group(key, def) === section && (showUnused || key === 'fetalAnatomy' || report.fields[key] !== undefined),
        );
        if (!rows.length) return null;
        return (
          <section key={section} aria-label={section}>
            <h4 className="mb-3 border-b bg-muted/40 px-3 py-2 font-semibold">{section}</h4>
            {rows.every(([, def]) => def.kind === 'measurement') ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-2">Measurement</th>
                      <th>Value</th>
                      <th>Unit</th>
                      {section === 'Biometry' && <th>GA (days)</th>}
                      <th>Percentile</th>
                      <th>Source / review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(([key, def]) => {
                      const current = report.fields[key];
                      const measurement = typeof current === 'object' ? current : undefined;
                      const units = def.schema.properties?.unit?.enum || [];
                      const change = (part: string, value: string) => {
                        if (part === 'value' && value === '') {
                          update(key, undefined);
                          return;
                        }
                        if (!measurement && part !== 'value') return;
                        const next = {
                          ...measurement,
                          value: measurement?.value ?? Number(value),
                          unit: measurement?.unit || units[0] || '',
                          source: 'manual',
                          needsReview: false,
                        } as Measurement;
                        if (part === 'unit') next.unit = value;
                        else if (part === 'value') next.value = Number(value);
                        else if (part === 'gaDays') {
                          if (value === '') delete next.gaDays;
                          else next.gaDays = Number(value);
                        } else if (part === 'percentile') {
                          if (value === '') delete next.percentile;
                          else next.percentile = Number(value);
                        }
                        update(key, next);
                      };
                      return (
                        <tr key={key} className="border-b">
                          <th scope="row" className="p-2 text-left font-normal">
                            {def.label}
                          </th>
                          <td className="p-1">
                            <Input
                              className="min-w-24"
                              type="number"
                              min="0"
                              step="any"
                              aria-label={`${def.label} value`}
                              value={measurement?.value ?? ''}
                              disabled={disabled}
                              onChange={(e) => change('value', e.target.value)}
                            />
                          </td>
                          <td className="p-1">
                            <select
                              className="rounded border bg-background p-2"
                              aria-label={`${def.label} unit`}
                              value={measurement?.unit || units[0] || ''}
                              disabled={disabled || !measurement}
                              onChange={(e) => change('unit', e.target.value)}>
                              {units.map((unit) => (
                                <option key={unit}>{unit}</option>
                              ))}
                            </select>
                          </td>
                          {section === 'Biometry' && (
                          <td className="p-1">
                            <Input
                              className="min-w-20"
                              type="number"
                              min="0"
                              step="1"
                              aria-label={`${def.label} GA days`}
                              value={measurement?.gaDays ?? ''}
                              disabled={disabled || !measurement}
                              onChange={(e) => change('gaDays', e.target.value)}
                            />
                          </td>
                          )}
                          <td className="p-1">
                            <Input
                              className="min-w-20"
                              type="number"
                              min="0"
                              max="100"
                              step="any"
                              aria-label={`${def.label} percentile`}
                              value={measurement?.percentile ?? ''}
                              disabled={disabled || !measurement}
                              onChange={(e) => change('percentile', e.target.value)}
                            />
                          </td>
                          <td className="p-2 text-xs">
                            {measurement ? (
                              <>
                                <span>
                                  {{
                                    pixel_ocr: 'Report image extraction',
                                    pdf_text: 'Original PDF text',
                                    dicom_sr: 'DICOM SR',
                                    manual: 'Manually corrected',
                                    derived: 'Calculated',
                                  }[measurement.source || ''] || 'Source'}
                                </span>
                                {measurement.needsReview && (
                                  <label className="mt-1 flex items-center gap-2">
                                    <Checkbox
                                      disabled={disabled}
                                      checked={false}
                                      aria-label={`Verify ${def.label}`}
                                      onCheckedChange={(v) => {
                                        if (v) update(key, { ...measurement, needsReview: false });
                                      }}
                                    />
                                    Verified against source
                                  </label>
                                )}
                              </>
                            ) : (
                              'Not supplied'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {rows.map(([key, def]) => (
                  <Field key={key} className={key === 'fetalAnatomy' ? 'sm:col-span-2' : undefined}>
                    <FieldLabel className="text-xs uppercase text-muted-foreground" htmlFor={`report-${key}`}>
                      {def.label}
                    </FieldLabel>
                    {key === 'fetalAnatomy' ? (
                      <>
                        <AnatomyFields value={String(report.fields[key] || '')} disabled={disabled || !!def.readOnly}
                          onChange={value => update(key, value)} />
                        {!disabled && !def.readOnly && (
                          <details className="print:hidden">
                            <summary className="cursor-pointer text-sm text-muted-foreground">Edit anatomy text</summary>
                            <Textarea
                              id={`report-${key}`}
                              aria-label="Edit anatomy text"
                              className="mt-2 min-h-24"
                              rows={Math.min(20, String(report.fields[key] || '').split('\n').length + 1)}
                              value={String(report.fields[key] ?? '')}
                              onChange={(e) => update(key, e.target.value)}
                            />
                          </details>
                        )}
                      </>
                    ) : def.schema.type === 'integer' ? (
                      <select
                        id={`report-${key}`}
                        className="rounded border bg-background p-2"
                        value={String(report.fields[key] ?? '')}
                        disabled={disabled || def.readOnly}
                        onChange={(e) => update(key, e.target.value === '' ? undefined : Number(e.target.value))}>
                        <option value="">Not supplied</option>
                        {(def.schema.enum || [0, 2]).map((v) => (
                          <option value={v} key={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Textarea
                        id={`report-${key}`}
                        className="min-h-10 resize-y rounded-none border-transparent bg-transparent px-0 shadow-none hover:border-input focus:border-input"
                        rows={
                          key === 'fetalAnatomy'
                            ? Math.min(20, String(report.fields[key] || '').split('\n').length + 1)
                            : 1
                        }
                        value={String(report.fields[key] ?? '')}
                        placeholder="Not supplied"
                        readOnly={def.readOnly}
                        disabled={disabled}
                        onChange={(e) => update(key, e.target.value)}
                      />
                    )}
                  </Field>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function UltrasoundSignatureArea({ form, report }: { form: ReportForm; report: ReportPayload }) {
  const signature = form.presentation?.signature;
  return (
    <section aria-label="Reading provider signature" className="mt-8 flex flex-col gap-3 border-t pt-5">
      <h4 className="font-semibold">Reading provider</h4>
      {signature ? (
        <>
          <img
            src={signature.image_url}
            alt="Reading provider signature"
            className="max-h-24 max-w-64 self-start object-contain"
          />
          <p>
            {signature.name}
            {signature.credentials ? `, ${signature.credentials}` : ''}
          </p>
          {signature.text && <p className="whitespace-pre-wrap">{signature.text}</p>}
          <p className="text-sm text-muted-foreground">
            Electronically signed · {new Date(signature.signed_at).toLocaleString()}
          </p>
        </>
      ) : (
        <>
          <div className="h-20 max-w-64 border-b" aria-hidden="true" />
          <p>{String(report.fields.readingPhysician || 'Reading provider name and credentials')}</p>
          <p className="text-sm text-muted-foreground">
            Not signed. The provider’s saved signature will be applied upon signing.
          </p>
        </>
      )}
    </section>
  );
}
